<ng-container *transloco="let t">
  <div id="migration-password-complexity-card" class="bg-white shadow-card w-full max-w-[440px] min-w-0 p-8 sm:p-10">
    <div id="complexity-header" class="flex flex-col items-center gap-2 mb-6">
      <img id="complexity-logo" [src]="logoUrl()" [alt]="partnerDisplayName()" class="h-12 w-auto" />
    </div>

    <p id="complexity-email-display" class="font-dm text-sm text-ld-neutral-700 m-0 mb-2">
      {{ email() }}
    </p>
    <h1 id="complexity-sign-in-title" class="text-xl font-semibold text-ld-neutral-700 m-0 mb-1">
      {{ t('commonLabelsSignIn') }}
    </h1>

    <div id="complexity-notice" class="mb-6">
      <h2 id="complexity-notice-subtitle" class="font-dm font-semibold text-base leading-6 text-ld-primary m-0 mb-3">
        {{ t('authMigrationPasswordUpdateRequired') }}
      </h2>
      <p id="complexity-notice-description" class="font-dm text-sm leading-5 text-ld-neutral-500 m-0">
        {{ t('authMigrationPasswordComplexityDescription') }}
      </p>
    </div>

    @if (externalErrorMessage()) {
      <div
        id="complexity-external-error"
        class="mb-4 rounded border border-red-200 bg-red-50 p-3 text-center"
        role="alert"
      >
        <p class="m-0 font-dm text-sm text-red-700">{{ externalErrorMessage() }}</p>
      </div>
    }

    <form id="complexity-form" class="flex flex-col gap-5" (ngSubmit)="onSubmit()">
      <div class="ms-input-group">
        @if (password().length > 0) {
          <label id="complexity-new-password-label" for="complexity-new-password" class="ms-input-label">
            {{ t('authMigrationPasswordComplexityNewPassword') }}
          </label>
        }
        <div class="ms-input-wrapper">
          <input
            id="complexity-new-password"
            [type]="hidePassword() ? 'password' : 'text'"
            class="ms-input"
            [placeholder]="password().length === 0 ? t('authMigrationPasswordComplexityNewPassword') : ''"
            [value]="password()"
            (input)="onPasswordInput($event)"
            autocomplete="new-password"
            aria-required="true"
            glance_masked="true"
          />
          <button
            type="button"
            id="complexity-toggle-password"
            (click)="togglePasswordVisibility()"
            class="flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-ld-neutral-700"
            [attr.aria-label]="hidePassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
          >
            <mat-icon class="text-base!">{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </div>
      </div>

      <div
        id="complexity-requirements"
        class="password-rules"
        role="list"
        [attr.aria-label]="t('authMigrationPasswordComplexityRequirementsTitle')"
      >
        <p id="complexity-rules-title" role="presentation" class="password-rules__title m-0 col-span-2">
          {{ t('authMigrationPasswordComplexityRequirementsTitle') }}
        </p>
        @for (rule of passwordValidationRules(); track rule.key) {
          <span
            role="listitem"
            class="password-rule"
            [class.password-rule--pass]="rule.valid"
            [class.password-rule--fail]="password().length > 0 && !rule.valid"
            [id]="'complexity-rule-' + rule.key"
          >
            @if (password().length > 0) {
              <mat-icon class="password-rule__icon">
                {{ rule.valid ? 'check' : 'close' }}
              </mat-icon>
            }
            {{ t(rule.label) }}
          </span>
        }
      </div>

      <div class="ms-input-group">
        @if (confirmPassword().length > 0) {
          <label id="complexity-confirm-password-label" for="complexity-confirm-password" class="ms-input-label">
            {{ t('authMigrationPasswordComplexityConfirmPassword') }}
          </label>
        }
        <div class="ms-input-wrapper">
          <input
            id="complexity-confirm-password"
            [type]="hideConfirmPassword() ? 'password' : 'text'"
            class="ms-input"
            [placeholder]="confirmPassword().length === 0 ? t('authMigrationPasswordComplexityConfirmPassword') : ''"
            [value]="confirmPassword()"
            (input)="onConfirmPasswordInput($event)"
            (keydown.enter)="$event.preventDefault()"
            autocomplete="new-password"
            aria-required="true"
            glance_masked="true"
          />
          <button
            type="button"
            id="complexity-toggle-confirm-password"
            (click)="toggleConfirmPasswordVisibility()"
            class="flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-ld-neutral-700"
            [attr.aria-label]="hideConfirmPassword() ? t('commonAriaShowPassword') : t('commonAriaHidePassword')"
          >
            <mat-icon class="text-base!">{{ hideConfirmPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </div>
        @if (passwordMismatch()) {
          <span id="complexity-mismatch-error" class="font-dm text-xs text-red-500" role="alert">
            {{ t('authRegisterCiamPasswordMismatch') }}
          </span>
        }
      </div>

      @if (passwordMatchesEmail()) {
        <span id="complexity-email-match-error" class="font-dm text-xs text-red-500" role="alert">
          {{ t('authMigrationPasswordCannotMatchEmail') }}
        </span>
      }

      <button
        type="submit"
        id="complexity-submit-button"
        class="w-full h-11 bg-ld-entra text-white border-0 font-dm font-semibold text-base cursor-pointer transition-all hover:bg-ld-entra-hover active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-ld-entra focus-visible:outline-offset-2 flex items-center justify-center disabled:bg-[#9391a1] disabled:cursor-not-allowed"
        [disabled]="!isFormValid() || isLoading()"
      >
        @if (isLoading()) {
          <span class="mr-2">{{ t('authLoginSigningIn') }}</span>
        } @else {
          {{ t('authMigrationPasswordComplexitySubmitButton') }}
        }
      </button>
    </form>
  </div>
</ng-container>
